import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, Download, Edit, Eye, Link as LinkIcon, Phone, Plus, Printer, Trash2, Upload, Users } from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  createRegistrationPage,
  deleteRegistration,
  listAdminRegistrationPages,
  listAdminRegistrations,
  updateRegistrationPage,
  updateRegistrationStatus,
} from '@/api/eventRegistrationService';
import { uploadImage } from '@/api/contentService';
import { toast } from 'sonner';
import { exportCsvFile } from '@/utils/csvExport';

const publicRegistrationPath = '/inscricao-evento';

const emptyPage = {
  title: '',
  slug: '',
  description: '',
  event_date: '',
  start_date: '',
  end_date: '',
  start_time: '',
  end_time: '',
  location: '',
  price: '',
  max_spots: '',
  allow_waitlist: true,
  adult_price: '',
  age_price_rules: [],
  custom_questions: [],
  is_active: true,
  allow_family: true,
  require_cpf: true,
  require_address: true,
  payment_instructions: '',
  terms_text: '',
  terms_url: '',
  authorization_term_url: '',
  confirmation_message: '',
};

const paymentStatuses = ['Pendente', 'Em andamento', 'Concluido', 'Cancelado'];
const paymentMethods = ['Dinheiro', 'Pix', 'Cartao', 'Boleto', 'Rifa', 'Isento'];
const registrationStatuses = ['Recebida', 'Confirmada', 'Pendente', 'Lista de espera', 'Cancelada'];
const authorizationStatuses = ['Nao se aplica', 'Pendente', 'Aprovada', 'Rejeitada'];

function formatDate(date) {
  if (!date) return 'Sem data';
  const [year, month, day] = String(date).slice(0, 10).split('-');
  return day && month && year ? `${day}/${month}/${year}` : date;
}

function formatTime(time) {
  if (!time) return null;
  return String(time).slice(0, 5);
}

function formatEventPeriod(page) {
  const startDate = page.start_date || page.event_date;
  const endDate = page.end_date;
  const startLabel = formatDate(startDate);
  const endLabel = endDate && endDate !== startDate ? formatDate(endDate) : null;
  const startTime = formatTime(page.start_time);
  const endTime = formatTime(page.end_time);
  const dateText = endLabel ? `${startLabel} a ${endLabel}` : startLabel;
  const timeText = startTime && endTime ? `${startTime} às ${endTime}` : startTime;
  return timeText ? `${dateText} · ${timeText}` : dateText;
}

function formatMoney(value) {
  if (value == null || value === '') return 'Valor não informado';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function getGrossAmount(registration) {
  return Number(registration?.total_amount || 0);
}

function getDiscountAmount(registration) {
  return Math.min(Math.max(Number(registration?.discount_amount || 0), 0), getGrossAmount(registration));
}

function getNetAmount(registration) {
  return Math.max(getGrossAmount(registration) - getDiscountAmount(registration), 0);
}

function statusClass(status) {
  if (['Concluido', 'Confirmada', 'Aprovada'].includes(status)) return 'bg-emerald-100 text-emerald-700';
  if (['Pendente', 'Em andamento', 'Recebida'].includes(status)) return 'bg-amber-100 text-amber-700';
  if (['Cancelado', 'Cancelada', 'Rejeitada'].includes(status)) return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-700';
}

function normalizePhone(phone) {
  return String(phone || '').replace(/\D/g, '');
}

function hasMinor(registration) {
  return (Array.isArray(registration.participants) ? registration.participants : []).some((participant) => participant.is_minor);
}

function formatAnswerValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const answer = value.answer ? `Resposta: ${value.answer}` : '';
    const reason = value.reason ? `Motivo: ${value.reason}` : '';
    return [answer, reason].filter(Boolean).join(' · ') || 'Sem resposta';
  }
  if (typeof value === 'boolean') return value ? 'Sim' : 'Nao';
  return String(value ?? '');
}

function getAnswerFilterValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return String(value.answer || '').trim();
  if (typeof value === 'boolean') return value ? 'Sim' : 'Nao';
  return String(value ?? '').trim();
}

export default function AdminInscricoes() {
  const [showPageForm, setShowPageForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [selectedPageId, setSelectedPageId] = useState('all');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [authorizationFilter, setAuthorizationFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [minorFilter, setMinorFilter] = useState('all');
  const [additionalQuestionFilter, setAdditionalQuestionFilter] = useState('all');
  const [additionalAnswerFilter, setAdditionalAnswerFilter] = useState('all');
  const [formData, setFormData] = useState(emptyPage);
  const [uploadingTerm, setUploadingTerm] = useState(false);
  const [uploadingRegistrationTerm, setUploadingRegistrationTerm] = useState(false);
  const queryClient = useQueryClient();

  const { data: pages = [], isLoading: loadingPages } = useQuery({
    queryKey: ['adminRegistrationPages'],
    queryFn: () => listAdminRegistrationPages(),
  });

  const { data: registrations = [], isLoading: loadingRegistrations } = useQuery({
    queryKey: ['adminEventRegistrations', selectedPageId],
    queryFn: () => listAdminRegistrations(selectedPageId),
  });

  const pageById = useMemo(() => new Map(pages.map((page) => [page.id, page])), [pages]);
  const additionalQuestionOptions = useMemo(() => {
    const sourcePages = selectedPageId === 'all' ? pages : pages.filter((page) => page.id === selectedPageId);
    return sourcePages.flatMap((page) => (
      (Array.isArray(page.custom_questions) ? page.custom_questions : [])
        .filter((question) => question.id && question.label)
        .map((question) => ({
          value: `${page.id}::${question.id}`,
          pageId: page.id,
          questionId: question.id,
          label: selectedPageId === 'all' ? `${page.title}: ${question.label}` : question.label,
        }))
    ));
  }, [pages, selectedPageId]);
  const selectedAdditionalQuestion = additionalQuestionOptions.find((question) => question.value === additionalQuestionFilter);
  const baseFilteredRegistrations = useMemo(() => registrations.filter((registration) => {
    if (statusFilter !== 'all' && registration.registration_status !== statusFilter) return false;
    if (paymentFilter !== 'all' && registration.payment_status !== paymentFilter) return false;
    if (authorizationFilter !== 'all' && registration.authorization_status !== authorizationFilter) return false;
    if (paymentMethodFilter !== 'all' && registration.payment_method !== paymentMethodFilter) return false;
    if (minorFilter === 'minor' && !hasMinor(registration)) return false;
    if (minorFilter === 'adult' && hasMinor(registration)) return false;
    return true;
  }), [authorizationFilter, minorFilter, paymentFilter, paymentMethodFilter, registrations, statusFilter]);
  const additionalAnswerOptions = useMemo(() => {
    if (!selectedAdditionalQuestion) return [];
    const answers = new Set();
    baseFilteredRegistrations.forEach((registration) => {
      if (registration.page_id !== selectedAdditionalQuestion.pageId) return;
      const value = getAnswerFilterValue(registration.answers?.[selectedAdditionalQuestion.questionId]);
      if (value) answers.add(value);
    });
    return Array.from(answers).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [baseFilteredRegistrations, selectedAdditionalQuestion]);
  const filteredRegistrations = useMemo(() => baseFilteredRegistrations.filter((registration) => {
    if (!selectedAdditionalQuestion) return true;
    if (registration.page_id !== selectedAdditionalQuestion.pageId) return false;
    if (additionalAnswerFilter === 'all') return true;
    return getAnswerFilterValue(registration.answers?.[selectedAdditionalQuestion.questionId]) === additionalAnswerFilter;
  }), [additionalAnswerFilter, baseFilteredRegistrations, selectedAdditionalQuestion]);
  const financialSummary = useMemo(() => {
    const participantsCount = filteredRegistrations.reduce((sum, registration) => sum + (Array.isArray(registration.participants) ? registration.participants.length : 0), 0);
    const totalGross = filteredRegistrations.reduce((sum, registration) => sum + getGrossAmount(registration), 0);
    const totalDiscounts = filteredRegistrations.reduce((sum, registration) => sum + getDiscountAmount(registration), 0);
    const totalExpected = filteredRegistrations.reduce((sum, registration) => sum + getNetAmount(registration), 0);
    const totalPaid = filteredRegistrations
      .filter((registration) => registration.payment_status === 'Concluido')
      .reduce((sum, registration) => sum + getNetAmount(registration), 0);
    const byMethod = filteredRegistrations.reduce((acc, registration) => {
      const method = registration.payment_method || 'Nao informado';
      acc[method] = (acc[method] || 0) + 1;
      return acc;
    }, {});
    return {
      participantsCount,
      totalGross,
      totalDiscounts,
      totalExpected,
      totalPaid,
      totalPending: totalExpected - totalPaid,
      byMethod,
    };
  }, [filteredRegistrations]);

  const createMutation = useMutation({
    mutationFn: (data) => createRegistrationPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminRegistrationPages'] });
      queryClient.invalidateQueries({ queryKey: ['publicRegistrationPages'] });
      toast.success('Página de inscrição criada.');
      resetForm();
    },
    onError: (error) => toast.error(error?.message || 'Erro ao criar página.'),
  });

  const updatePageMutation = useMutation({
    mutationFn: ({ id, data }) => updateRegistrationPage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminRegistrationPages'] });
      queryClient.invalidateQueries({ queryKey: ['publicRegistrationPages'] });
      toast.success('Página de inscrição atualizada.');
      resetForm();
    },
    onError: (error) => toast.error(error?.message || 'Erro ao atualizar página.'),
  });

  const updateRegistrationMutation = useMutation({
    mutationFn: ({ id, data }) => updateRegistrationStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminEventRegistrations'] });
      toast.success('Inscrição atualizada.');
    },
    onError: (error) => toast.error(error?.message || 'Erro ao atualizar inscrição.'),
  });

  const deleteRegistrationMutation = useMutation({
    mutationFn: (id) => deleteRegistration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminEventRegistrations'] });
      queryClient.invalidateQueries({ queryKey: ['registrationAvailability'] });
      setSelectedRegistration(null);
      toast.success('Inscrição excluída.');
    },
    onError: (error) => toast.error(error?.message || 'Erro ao excluir inscrição.'),
  });

  const resetForm = () => {
    setFormData(emptyPage);
    setEditingPage(null);
    setShowPageForm(false);
  };

  const handleEditPage = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title || '',
      slug: page.slug || '',
      description: page.description || '',
      event_date: page.event_date || '',
      start_date: page.start_date || page.event_date || '',
      end_date: page.end_date || page.event_date || '',
      start_time: page.start_time || '',
      end_time: page.end_time || '',
      location: page.location || '',
      price: page.price ?? '',
      max_spots: page.max_spots ?? '',
      allow_waitlist: page.allow_waitlist ?? true,
      adult_price: page.adult_price ?? '',
      age_price_rules: Array.isArray(page.age_price_rules) ? page.age_price_rules : [],
      custom_questions: Array.isArray(page.custom_questions) ? page.custom_questions : [],
      is_active: page.is_active ?? true,
      allow_family: page.allow_family ?? true,
      require_cpf: page.require_cpf ?? true,
      require_address: page.require_address ?? true,
      payment_instructions: page.payment_instructions || '',
      terms_text: page.terms_text || '',
      terms_url: page.terms_url || '',
      authorization_term_url: page.authorization_term_url || '',
      confirmation_message: page.confirmation_message || '',
    });
    setShowPageForm(true);
  };

  const handleSavePage = () => {
    if (!formData.title) {
      toast.error('Informe o título da inscrição.');
      return;
    }
    if (formData.end_date && formData.start_date && formData.end_date < formData.start_date) {
      toast.error('A data de fim não pode ser antes da data de início.');
      return;
    }

    if (editingPage) {
      updatePageMutation.mutate({ id: editingPage.id, data: formData });
      return;
    }

    createMutation.mutate(formData);
  };

  const copyPublicLink = async (page) => {
    const path = `${publicRegistrationPath}?evento=${page.slug}`;
    const url = `${window.location.origin}${path}`;
    await navigator.clipboard.writeText(url);
    toast.success('Link copiado.');
  };

  const handleTermUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingTerm(true);
    try {
      const fileUrl = await uploadImage(file, 'registration-terms');
      setFormData((current) => ({ ...current, authorization_term_url: fileUrl }));
      toast.success('Termo enviado.');
    } catch (error) {
      toast.error(error?.message || 'Erro ao enviar termo.');
    } finally {
      setUploadingTerm(false);
    }
  };

  const handleRegistrationTermUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingRegistrationTerm(true);
    try {
      const fileUrl = await uploadImage(file, 'registration-terms');
      setFormData((current) => ({ ...current, terms_url: fileUrl }));
      toast.success('Termo da inscrição enviado.');
    } catch (error) {
      toast.error(error?.message || 'Erro ao enviar termo da inscrição.');
    } finally {
      setUploadingRegistrationTerm(false);
    }
  };

  const exportCsv = async () => {
    if (filteredRegistrations.length === 0) {
      toast.error('Não há inscrições para exportar.');
      return;
    }

    const customQuestionColumns = [];
    const seenQuestionColumns = new Set();
    filteredRegistrations.forEach((registration) => {
      const page = pageById.get(registration.page_id);
      const questions = Array.isArray(page?.custom_questions) ? page.custom_questions : [];
      questions.forEach((question) => {
        if (!question.id || !question.label) return;
        const key = `${registration.page_id}::${question.id}`;
        if (seenQuestionColumns.has(key)) return;
        seenQuestionColumns.add(key);
        customQuestionColumns.push({
          pageId: registration.page_id,
          questionId: question.id,
          label: selectedPageId === 'all' ? `${page?.title || 'Evento'} - ${question.label}` : question.label,
        });
      });

      Object.keys(registration.answers || {}).forEach((answerKey) => {
        const key = `${registration.page_id}::${answerKey}`;
        if (seenQuestionColumns.has(key)) return;
        seenQuestionColumns.add(key);
        customQuestionColumns.push({
          pageId: registration.page_id,
          questionId: answerKey,
          label: selectedPageId === 'all' ? `${page?.title || 'Evento'} - ${answerKey}` : answerKey,
        });
      });
    });

    const rows = [
      [
        'codigo',
        'evento',
        'responsavel',
        'telefone',
        'email',
        'participante',
        'sexo',
        'cpf',
        'nascimento',
        'menor',
        'responsavel_menor',
        'pagamento',
        'parcelas',
        'status_pagamento',
        'status_inscricao',
        'status_autorizacao',
        'termo_assinado_enviado_em',
        'valor_original',
        'desconto',
        'motivo_desconto',
        'valor_final',
        'lista_espera',
        ...customQuestionColumns.map((question) => `info_adicional_${question.label}`),
      ],
    ];

    filteredRegistrations.forEach((registration) => {
      const page = pageById.get(registration.page_id);
      const participants = Array.isArray(registration.participants) ? registration.participants : [];
      participants.forEach((participant) => {
        rows.push([
          registration.registration_code || '',
          page?.title || '',
          registration.contact_name || '',
          registration.contact_phone || '',
          registration.contact_email || '',
          participant.full_name || '',
          participant.gender || '',
          participant.cpf || '',
          participant.birth_date || '',
          participant.is_minor ? 'Sim' : 'Nao',
          participant.guardian_name || '',
          registration.payment_method || '',
          registration.payment_installments || 1,
          registration.payment_status || '',
          registration.registration_status || '',
          registration.authorization_status || '',
          registration.authorization_uploaded_at ? new Date(registration.authorization_uploaded_at).toLocaleString('pt-BR') : '',
          getGrossAmount(registration),
          getDiscountAmount(registration),
          registration.discount_reason || '',
          getNetAmount(registration),
          registration.is_waitlist ? 'Sim' : 'Nao',
          ...customQuestionColumns.map((question) => (
            registration.page_id === question.pageId
              ? formatAnswerValue(registration.answers?.[question.questionId])
              : ''
          )),
        ]);
      });
    });

    try {
      await exportCsvFile(rows, `inscricoes-${selectedPageId}-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Relatório gerado.');
    } catch (error) {
      toast.error(error?.message || 'Não foi possível gerar o relatório.');
    }
  };

  const updateRegistration = (registration, field, value) => {
    updateRegistrationMutation.mutate({
      id: registration.id,
      data: { [field]: value },
    });
  };

  const handleDeleteRegistration = (registration) => {
    if (!registration?.id) return;
    const label = registration.registration_code || registration.contact_name || 'esta inscrição';
    if (confirm(`Excluir definitivamente ${label}? Essa ação não pode ser desfeita.`)) {
      deleteRegistrationMutation.mutate(registration.id);
    }
  };

  const addQuestion = () => {
    setFormData((current) => ({
      ...current,
      custom_questions: [
        ...(Array.isArray(current.custom_questions) ? current.custom_questions : []),
        {
          id: `q_${Date.now()}`,
          label: '',
          required: false,
          placeholder: '',
          reason_placeholder: '',
          option_a_label: '',
          option_b_label: '',
          type: 'text',
        },
      ],
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      custom_questions: current.custom_questions.map((question, questionIndex) => (
        questionIndex === index ? { ...question, [field]: value } : question
      )),
    }));
  };

  const removeQuestion = (index) => {
    setFormData((current) => ({
      ...current,
      custom_questions: current.custom_questions.filter((_, questionIndex) => questionIndex !== index),
    }));
  };

  const addAgePriceRule = () => {
    setFormData((current) => ({
      ...current,
      age_price_rules: [
        ...(Array.isArray(current.age_price_rules) ? current.age_price_rules : []),
        { min_age: '', max_age: '', price: '', label: '' },
      ],
    }));
  };

  const updateAgePriceRule = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      age_price_rules: current.age_price_rules.map((rule, ruleIndex) => (
        ruleIndex === index ? { ...rule, [field]: value } : rule
      )),
    }));
  };

  const removeAgePriceRule = (index) => {
    setFormData((current) => ({
      ...current,
      age_price_rules: current.age_price_rules.filter((_, ruleIndex) => ruleIndex !== index),
    }));
  };

  const openWhatsApp = (phone, name) => {
    const normalized = normalizePhone(phone);
    if (!normalized) {
      toast.error('Telefone não informado.');
      return;
    }
    const message = encodeURIComponent(`Olá ${name || ''}, estamos falando sobre sua inscrição no evento.`);
    window.open(`https://wa.me/55${normalized}?text=${message}`, '_blank', 'noopener,noreferrer');
  };

  const printRegistration = (registration) => {
    const page = pageById.get(registration.page_id);
    const participants = Array.isArray(registration.participants) ? registration.participants : [];
    const answers = registration.answers || {};
    const html = `
      <html>
        <head>
          <title>Ficha de inscrição</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; padding: 24px; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            h2 { font-size: 16px; margin-top: 22px; border-bottom: 1px solid #ddd; padding-bottom: 6px; }
            p { margin: 4px 0; font-size: 13px; }
            .box { border: 1px solid #ddd; padding: 12px; margin-top: 10px; border-radius: 8px; }
          </style>
        </head>
        <body>
          <h1>${page?.title || 'Evento'}</h1>
          <p><strong>Código:</strong> ${registration.registration_code || registration.id}</p>
          <p><strong>Responsável:</strong> ${registration.contact_name || ''}</p>
          <p><strong>Telefone:</strong> ${registration.contact_phone || ''}</p>
          <p><strong>Status:</strong> ${registration.registration_status || ''} · Pagamento: ${registration.payment_status || ''} · Autorização: ${registration.authorization_status || ''}</p>
          <p><strong>Forma de pagamento:</strong> ${registration.payment_method || 'Forma não informada'} · ${registration.payment_installments || 1} parcela(s)</p>
          <p><strong>Valor original:</strong> ${formatMoney(getGrossAmount(registration))}</p>
          <p><strong>Desconto:</strong> ${formatMoney(getDiscountAmount(registration))}</p>
          <p><strong>Total final:</strong> ${formatMoney(getNetAmount(registration))}</p>
          ${registration.discount_reason ? `<p><strong>Motivo do desconto:</strong> ${registration.discount_reason}</p>` : ''}
          <h2>Participantes</h2>
          ${participants.map((participant) => `
            <div class="box">
              <p><strong>${participant.full_name || ''}</strong></p>
              <p>${participant.relationship || ''} · ${participant.gender || ''} · ${participant.birth_date || ''}</p>
              <p>CPF: ${participant.cpf || ''} · Telefone: ${participant.phone || ''}</p>
              <p>Endereço: ${participant.address || ''}</p>
              ${participant.is_minor ? `<p>Responsável menor: ${participant.guardian_name || ''} · ${participant.guardian_phone || ''}</p>` : ''}
            </div>
          `).join('')}
          <h2>Respostas</h2>
          ${Object.entries(answers).map(([key, value]) => `<p><strong>${key}:</strong> ${formatAnswerValue(value)}</p>`).join('') || '<p>Sem respostas extras.</p>'}
          <h2>Observações</h2>
          <p>${registration.notes || ''}</p>
          <p>${registration.admin_notes || ''}</p>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (loadingPages) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-emerald-700 to-slate-900 text-white pt-8 pb-6 px-4">
        <div className="max-w-5xl mx-auto">
          <PageHeader
            title="Inscrições"
            subtitle="Eventos, vagas e acompanhamento"
            showBack={true}
            action={
              <Button size="sm" variant="secondary" onClick={() => setShowPageForm(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Nova
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {pages.length === 0 ? (
          <EmptyState
            icon={CalendarCheck}
            title="Nenhuma página de inscrição"
            description="Crie uma página para receber inscrições públicas."
            actionLabel="Criar inscrição"
            onAction={() => setShowPageForm(true)}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pages.map((page) => (
                <div key={page.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{page.title}</h3>
                        <Badge className={page.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                          {page.is_active ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{formatEventPeriod(page)} · {formatMoney(page.price)}</p>
                      <p className="text-sm text-gray-500">{page.max_spots ? `${page.max_spots} vaga(s)` : 'Sem limite de vagas'}</p>
                      <p className="text-xs text-gray-500 mt-1">{publicRegistrationPath}?evento={page.slug}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => copyPublicLink(page)}>
                        <LinkIcon className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleEditPage(page)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-semibold text-gray-900">Inscrições recebidas</h2>
                  <p className="text-sm text-gray-500">{filteredRegistrations.length} de {registrations.length} registro(s)</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={exportCsv}>
                    <Download className="w-4 h-4 mr-2" />
                    Exportar
                  </Button>
                  <Select value={selectedPageId} onValueChange={(value) => {
                    setSelectedPageId(value);
                    setAdditionalQuestionFilter('all');
                    setAdditionalAnswerFilter('all');
                  }}>
                    <SelectTrigger className="sm:w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os eventos</SelectItem>
                      {pages.map((page) => (
                        <SelectItem key={page.id} value={page.id}>{page.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Participantes</p>
                  <p className="text-xl font-bold text-gray-900">{financialSummary.participantsCount}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Bruto</p>
                  <p className="text-xl font-bold text-gray-900">{formatMoney(financialSummary.totalGross)}</p>
                </div>
                <div className="rounded-lg bg-rose-50 p-3">
                  <p className="text-xs text-rose-700">Descontos</p>
                  <p className="text-xl font-bold text-rose-900">{formatMoney(financialSummary.totalDiscounts)}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Final previsto</p>
                  <p className="text-xl font-bold text-gray-900">{formatMoney(financialSummary.totalExpected)}</p>
                </div>
                <div className="rounded-lg bg-emerald-50 p-3">
                  <p className="text-xs text-emerald-700">Concluído</p>
                  <p className="text-xl font-bold text-emerald-900">{formatMoney(financialSummary.totalPaid)}</p>
                </div>
                <div className="rounded-lg bg-amber-50 p-3">
                  <p className="text-xs text-amber-700">Pendente</p>
                  <p className="text-xl font-bold text-amber-900">{formatMoney(financialSummary.totalPending)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2 mb-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos status</SelectItem>
                    {registrationStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                  <SelectTrigger><SelectValue placeholder="Pagamento" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos pagamentos</SelectItem>
                    {paymentStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={authorizationFilter} onValueChange={setAuthorizationFilter}>
                  <SelectTrigger><SelectValue placeholder="Autorização" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas autorizações</SelectItem>
                    {authorizationStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                  <SelectTrigger><SelectValue placeholder="Forma" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas formas</SelectItem>
                    {paymentMethods.map((method) => <SelectItem key={method} value={method}>{method === 'Cartao' ? 'Cartão' : method}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={minorFilter} onValueChange={setMinorFilter}>
                  <SelectTrigger><SelectValue placeholder="Idade" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="minor">Com menor</SelectItem>
                    <SelectItem value="adult">Sem menor</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={additionalQuestionFilter} onValueChange={(value) => {
                  setAdditionalQuestionFilter(value);
                  setAdditionalAnswerFilter('all');
                }}>
                  <SelectTrigger><SelectValue placeholder="Informação adicional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas informações</SelectItem>
                    {additionalQuestionOptions.map((question) => (
                      <SelectItem key={question.value} value={question.value}>{question.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={additionalAnswerFilter} onValueChange={setAdditionalAnswerFilter} disabled={!selectedAdditionalQuestion}>
                  <SelectTrigger><SelectValue placeholder="Resposta" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas respostas</SelectItem>
                    {additionalAnswerOptions.map((answer) => (
                      <SelectItem key={answer} value={answer}>{answer === 'Nao' ? 'Não' : answer}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <p className="text-xs text-gray-500 mb-4">
                Formas: {Object.entries(financialSummary.byMethod).map(([method, count]) => `${method}: ${count}`).join(' · ') || 'sem registros'}
                {selectedAdditionalQuestion && additionalAnswerFilter !== 'all' ? ` · ${selectedAdditionalQuestion.label}: ${additionalAnswerFilter === 'Nao' ? 'Não' : additionalAnswerFilter}` : ''}
              </p>

              {loadingRegistrations ? (
                <LoadingSpinner />
              ) : filteredRegistrations.length === 0 ? (
                <EmptyState icon={Users} title="Nenhuma inscrição recebida" description="As inscrições públicas aparecerão aqui." />
              ) : (
                <div className="space-y-3">
                  {filteredRegistrations.map((registration) => {
                    const participants = Array.isArray(registration.participants) ? registration.participants : [];
                    const page = pageById.get(registration.page_id);

                    return (
                      <div key={registration.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900">{registration.contact_name}</h3>
                            {registration.registration_code && <p className="text-xs font-medium text-emerald-700">{registration.registration_code}</p>}
                            <p className="text-sm text-gray-500">{page?.title || 'Evento'} · {participants.length} participante(s)</p>
                            <p className="text-sm text-gray-500">{registration.contact_phone || 'Sem telefone'} {registration.contact_email ? `· ${registration.contact_email}` : ''}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge className={statusClass(registration.registration_status)}>{registration.registration_status}</Badge>
                              {registration.is_waitlist && <Badge className="bg-amber-100 text-amber-700">Lista de espera</Badge>}
                              <Badge className={statusClass(registration.payment_status)}>Pagamento: {registration.payment_status}</Badge>
                              <Badge className={statusClass(registration.authorization_status)}>Autorização: {registration.authorization_status}</Badge>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 lg:w-[34rem]">
                            <Select value={registration.registration_status} onValueChange={(value) => updateRegistration(registration, 'registration_status', value)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {registrationStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Select value={registration.payment_status} onValueChange={(value) => updateRegistration(registration, 'payment_status', value)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {paymentStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Select value={registration.authorization_status} onValueChange={(value) => updateRegistration(registration, 'authorization_status', value)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {authorizationStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="mt-3">
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" onClick={() => setSelectedRegistration(registration)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver detalhes
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openWhatsApp(registration.contact_phone, registration.contact_name)}>
                              <Phone className="w-4 h-4 mr-2" />
                              WhatsApp
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => printRegistration(registration)}>
                              <Printer className="w-4 h-4 mr-2" />
                              Imprimir
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleDeleteRegistration(registration)}>
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </Button>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="font-medium text-gray-900 mb-2">Participantes</p>
                            <div className="space-y-2">
                              {participants.map((participant, index) => (
                                <div key={`${participant.full_name}-${index}`} className="text-gray-600">
                                  <p className="font-medium text-gray-800">{participant.full_name}</p>
                                  <p>{participant.relationship} · {participant.gender || 'Sexo não informado'} · {participant.birth_date || 'Sem nascimento'} · {participant.cpf || 'Sem CPF'}</p>
                                  {participant.is_minor && <p>Menor: {participant.guardian_name} · {participant.guardian_phone}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="font-medium text-gray-900 mb-2">Pagamento e autorização</p>
                            <p className="text-gray-600">{registration.payment_method || 'Forma não informada'} · {registration.payment_installments || 1} parcela(s)</p>
                            <p className="text-gray-600">Original: {formatMoney(getGrossAmount(registration))}</p>
                            {getDiscountAmount(registration) > 0 && (
                              <p className="text-rose-700">Desconto: {formatMoney(getDiscountAmount(registration))}{registration.discount_reason ? ` · ${registration.discount_reason}` : ''}</p>
                            )}
                            <p className="font-semibold text-gray-900">Final: {formatMoney(getNetAmount(registration))}</p>
                            <p className="text-gray-600">Termo aberto: {registration.terms_opened_at ? new Date(registration.terms_opened_at).toLocaleString('pt-BR') : 'Não registrado'}</p>
                            <p className="text-gray-600">Entrega autorização: {registration.authorization_delivery || 'Não se aplica'}</p>
                            <p className="text-gray-600">Termo assinado enviado: {registration.authorization_uploaded_at ? new Date(registration.authorization_uploaded_at).toLocaleString('pt-BR') : 'Não registrado'}</p>
                            {registration.authorization_file_url && (
                              <a className="text-emerald-700 font-medium" href={registration.authorization_file_url} target="_blank" rel="noreferrer">
                                Ver arquivo enviado
                              </a>
                            )}
                            {registration.notes && <p className="text-gray-600 mt-2">Obs: {registration.notes}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={showPageForm} onOpenChange={setShowPageForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingPage ? 'Editar inscrição' : 'Nova inscrição'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Título *</Label>
                <Input value={formData.title} onChange={(event) => setFormData({ ...formData, title: event.target.value })} placeholder="Retiro, conferência..." />
              </div>
              <div>
                <Label>Slug do link</Label>
                <Input value={formData.slug} onChange={(event) => setFormData({ ...formData, slug: event.target.value })} placeholder="retiro-2026" />
              </div>
            </div>

            <div>
              <Label>Descrição personalizada</Label>
              <Textarea value={formData.description} onChange={(event) => setFormData({ ...formData, description: event.target.value })} rows={3} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Data de início</Label>
                <Input type="date" value={formData.start_date} onChange={(event) => setFormData({ ...formData, start_date: event.target.value, event_date: event.target.value })} />
              </div>
              <div>
                <Label>Horário de início</Label>
                <Input type="time" value={formData.start_time} onChange={(event) => setFormData({ ...formData, start_time: event.target.value })} />
              </div>
              <div>
                <Label>Data de fim</Label>
                <Input type="date" value={formData.end_date} onChange={(event) => setFormData({ ...formData, end_date: event.target.value })} />
              </div>
              <div>
                <Label>Horário de fim</Label>
                <Input type="time" value={formData.end_time} onChange={(event) => setFormData({ ...formData, end_time: event.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Local</Label>
                <Input value={formData.location} onChange={(event) => setFormData({ ...formData, location: event.target.value })} />
              </div>
              <div>
                <Label>Valor</Label>
                <Input type="number" min="0" step="0.01" value={formData.price} onChange={(event) => setFormData({ ...formData, price: event.target.value })} />
              </div>
              <div>
                <Label>Vagas</Label>
                <Input type="number" min="1" value={formData.max_spots} onChange={(event) => setFormData({ ...formData, max_spots: event.target.value })} placeholder="Sem limite" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Valor adulto</Label>
                <Input type="number" min="0" step="0.01" value={formData.adult_price} onChange={(event) => setFormData({ ...formData, adult_price: event.target.value })} placeholder="Usa valor geral" />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                <div>
                  <Label>Lista de espera</Label>
                  <p className="text-xs text-gray-500">Permite cadastro após lotar.</p>
                </div>
                <Switch checked={formData.allow_waitlist} onCheckedChange={(checked) => setFormData({ ...formData, allow_waitlist: checked })} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ['is_active', 'Inscrição ativa'],
                ['allow_family', 'Permitir família/grupo'],
                ['require_cpf', 'CPF obrigatório'],
                ['require_address', 'Endereço obrigatório'],
              ].map(([field, label]) => (
                <div key={field} className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                  <Label>{label}</Label>
                  <Switch checked={formData[field]} onCheckedChange={(checked) => setFormData({ ...formData, [field]: checked })} />
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-gray-200 p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label>Valores por idade</Label>
                  <p className="text-xs text-gray-500 mt-1">Ex.: 0 a 12 isento, 13 a 15 valor especial.</p>
                </div>
                <Button size="sm" variant="outline" onClick={addAgePriceRule}>
                  <Plus className="w-4 h-4 mr-1" />
                  Faixa
                </Button>
              </div>

              {(formData.age_price_rules || []).map((rule, index) => (
                <div key={index} className="rounded-lg bg-gray-50 p-3 space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label>Idade inicial</Label>
                      <Input type="number" min="0" value={rule.min_age} onChange={(event) => updateAgePriceRule(index, 'min_age', event.target.value)} />
                    </div>
                    <div>
                      <Label>Idade final</Label>
                      <Input type="number" min="0" value={rule.max_age} onChange={(event) => updateAgePriceRule(index, 'max_age', event.target.value)} />
                    </div>
                    <div>
                      <Label>Valor</Label>
                      <Input type="number" min="0" step="0.01" value={rule.price} onChange={(event) => updateAgePriceRule(index, 'price', event.target.value)} placeholder="0 para isento" />
                    </div>
                    <div>
                      <Label>Nome</Label>
                      <Input value={rule.label || ''} onChange={(event) => updateAgePriceRule(index, 'label', event.target.value)} placeholder="Criança" />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => removeAgePriceRule(index)}>
                      Remover
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-gray-200 p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label>Perguntas personalizadas</Label>
                  <p className="text-xs text-gray-500 mt-1">Crie perguntas com resposta em texto, Sim/Não ou opções personalizadas com motivo.</p>
                </div>
                <Button size="sm" variant="outline" onClick={addQuestion}>
                  <Plus className="w-4 h-4 mr-1" />
                  Pergunta
                </Button>
              </div>

              {(formData.custom_questions || []).map((question, index) => (
                <div key={question.id} className="rounded-lg bg-gray-50 p-3 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label>Pergunta</Label>
                      <Input value={question.label} onChange={(event) => updateQuestion(index, 'label', event.target.value)} placeholder="Tem alguma restrição alimentar?" />
                    </div>
                    <div>
                      <Label>{['yes_no', 'custom_options'].includes(question.type) ? 'Texto do campo de motivo' : 'Placeholder'}</Label>
                      <Input
                        value={['yes_no', 'custom_options'].includes(question.type) ? question.reason_placeholder || '' : question.placeholder || ''}
                        onChange={(event) => updateQuestion(index, ['yes_no', 'custom_options'].includes(question.type) ? 'reason_placeholder' : 'placeholder', event.target.value)}
                        placeholder={['yes_no', 'custom_options'].includes(question.type) ? 'Explique o motivo da resposta' : 'Digite sua resposta'}
                      />
                    </div>
                  </div>
                  {question.type === 'custom_options' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label>Primeira opção</Label>
                        <Input value={question.option_a_label || ''} onChange={(event) => updateQuestion(index, 'option_a_label', event.target.value)} placeholder="Ônibus" />
                      </div>
                      <div>
                        <Label>Segunda opção</Label>
                        <Input value={question.option_b_label || ''} onChange={(event) => updateQuestion(index, 'option_b_label', event.target.value)} placeholder="Carro" />
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="sm:w-48">
                      <Label>Tipo</Label>
                      <Select value={question.type === 'checkbox' ? 'yes_no' : question.type || 'text'} onValueChange={(value) => updateQuestion(index, 'type', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Texto</SelectItem>
                          <SelectItem value="yes_no">Sim ou Não</SelectItem>
                          <SelectItem value="custom_options">Resposta personalizada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={question.required || false} onCheckedChange={(checked) => updateQuestion(index, 'required', checked)} />
                      <Label>Obrigatória</Label>
                    </div>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => removeQuestion(index)}>
                      Remover
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <Label>Orientações de pagamento</Label>
              <Textarea value={formData.payment_instructions} onChange={(event) => setFormData({ ...formData, payment_instructions: event.target.value })} rows={3} placeholder="Pagamento será realizado presencialmente na igreja..." />
            </div>

            <div>
              <Label>Termos/orientações da inscrição</Label>
              <Textarea value={formData.terms_text} onChange={(event) => setFormData({ ...formData, terms_text: event.target.value })} rows={4} placeholder="Regras do evento, autorização, prazos..." />
            </div>

            <div>
              <Label>Arquivo/link do termo da inscrição</Label>
              <div className="flex flex-col sm:flex-row gap-2 mt-1">
                <Input value={formData.terms_url} onChange={(event) => setFormData({ ...formData, terms_url: event.target.value })} placeholder="Link do termo que o inscrito precisa abrir" />
                <label className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                  <Upload className="w-4 h-4" />
                  {uploadingRegistrationTerm ? 'Enviando...' : 'Enviar'}
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleRegistrationTermUpload} disabled={uploadingRegistrationTerm} />
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Quando preenchido, o aceite dos termos só libera depois que a pessoa abrir este arquivo.
              </p>
            </div>

            <div>
              <Label>Termo de autorização para menores</Label>
              <div className="flex flex-col sm:flex-row gap-2 mt-1">
                <Input value={formData.authorization_term_url} onChange={(event) => setFormData({ ...formData, authorization_term_url: event.target.value })} placeholder="Link do termo" />
                <label className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                  <Upload className="w-4 h-4" />
                  {uploadingTerm ? 'Enviando...' : 'Enviar'}
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleTermUpload} disabled={uploadingTerm} />
                </label>
              </div>
            </div>

            <div>
              <Label>Mensagem de confirmação</Label>
              <Textarea value={formData.confirmation_message} onChange={(event) => setFormData({ ...formData, confirmation_message: event.target.value })} rows={3} placeholder="Mensagem exibida depois que a pessoa envia a inscrição." />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={resetForm}>Cancelar</Button>
              <Button className="flex-1" onClick={handleSavePage} disabled={createMutation.isPending || updatePageMutation.isPending}>
                {editingPage ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedRegistration} onOpenChange={(open) => !open && setSelectedRegistration(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da inscrição</DialogTitle>
          </DialogHeader>

          {selectedRegistration && (
            <div className="space-y-4 mt-4">
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                <p className="text-sm text-emerald-800">Código</p>
                <p className="text-xl font-bold text-emerald-900">{selectedRegistration.registration_code || selectedRegistration.id}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openWhatsApp(selectedRegistration.contact_phone, selectedRegistration.contact_name)}>
                  <Phone className="w-4 h-4 mr-2" />
                  WhatsApp responsável
                </Button>
                <Button size="sm" variant="outline" onClick={() => printRegistration(selectedRegistration)}>
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir ficha
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleDeleteRegistration(selectedRegistration)}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir inscrição
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Responsável</p>
                  <p className="font-medium text-gray-900">{selectedRegistration.contact_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Termo da inscrição</p>
                  <p className="font-medium text-gray-900">{selectedRegistration.terms_opened_at ? `Aberto em ${new Date(selectedRegistration.terms_opened_at).toLocaleString('pt-BR')}` : 'Não registrado'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Contato</p>
                  <p className="font-medium text-gray-900">{selectedRegistration.contact_phone || 'Sem telefone'} {selectedRegistration.contact_email ? `· ${selectedRegistration.contact_email}` : ''}</p>
                </div>
                <div>
                  <p className="text-gray-500">Termo assinado</p>
                  <p className="font-medium text-gray-900">{selectedRegistration.authorization_uploaded_at ? `Enviado em ${new Date(selectedRegistration.authorization_uploaded_at).toLocaleString('pt-BR')}` : 'Não registrado'}</p>
                  {selectedRegistration.authorization_file_url && (
                    <a className="text-emerald-700 font-medium" href={selectedRegistration.authorization_file_url} target="_blank" rel="noreferrer">
                      Ver arquivo enviado
                    </a>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Valor original</p>
                    <p className="font-semibold text-gray-900">{formatMoney(getGrossAmount(selectedRegistration))}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Desconto</p>
                    <p className="font-semibold text-rose-700">{formatMoney(getDiscountAmount(selectedRegistration))}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Valor final</p>
                    <p className="font-semibold text-emerald-700">{formatMoney(getNetAmount(selectedRegistration))}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Desconto</Label>
                    <Input
                      type="number"
                      min="0"
                      max={getGrossAmount(selectedRegistration)}
                      step="0.01"
                      value={selectedRegistration.discount_amount ?? ''}
                      onChange={(event) => setSelectedRegistration({ ...selectedRegistration, discount_amount: event.target.value })}
                      onBlur={(event) => {
                        const discountAmount = Math.min(Math.max(Number(event.target.value) || 0, 0), getGrossAmount(selectedRegistration));
                        setSelectedRegistration({ ...selectedRegistration, discount_amount: discountAmount });
                        updateRegistration(selectedRegistration, 'discount_amount', discountAmount);
                      }}
                      placeholder="0,00"
                    />
                  </div>
                  <div>
                    <Label>Motivo do desconto</Label>
                    <Input
                      value={selectedRegistration.discount_reason || ''}
                      onChange={(event) => setSelectedRegistration({ ...selectedRegistration, discount_reason: event.target.value })}
                      onBlur={(event) => updateRegistration(selectedRegistration, 'discount_reason', event.target.value)}
                      placeholder="Ex.: cortesia, ajuda, pagamento antecipado"
                    />
                  </div>
                </div>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-2">Participantes</p>
                <div className="space-y-2">
                  {(Array.isArray(selectedRegistration.participants) ? selectedRegistration.participants : []).map((participant, index) => (
                    <div key={`${participant.full_name}-${index}`} className="rounded-lg border border-gray-200 p-3 text-sm">
                      <p className="font-semibold text-gray-900">{participant.full_name}</p>
                      <p className="text-gray-600">{participant.relationship} · {participant.gender || 'Sem sexo'} · {participant.birth_date || 'Sem nascimento'}</p>
                      <p className="text-gray-600">CPF: {participant.cpf || 'Não informado'} · Telefone: {participant.phone || 'Não informado'}</p>
                      <p className="text-gray-600">Endereço: {participant.address || 'Não informado'}</p>
                      <p className="text-gray-600">Valor: {formatMoney(participant.amount || 0)}</p>
                      {participant.is_minor && (
                        <div className="text-amber-700">
                          <p>Menor: {participant.guardian_name} · {participant.guardian_phone}</p>
                          <Button size="sm" variant="outline" className="mt-2" onClick={() => openWhatsApp(participant.guardian_phone, participant.guardian_name)}>
                            <Phone className="w-4 h-4 mr-2" />
                            WhatsApp responsável menor
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-2">Respostas extras</p>
                <div className="rounded-lg border border-gray-200 p-3 text-sm space-y-1">
                  {Object.entries(selectedRegistration.answers || {}).length === 0 ? (
                    <p className="text-gray-500">Sem respostas extras.</p>
                  ) : (
                    Object.entries(selectedRegistration.answers || {}).map(([key, value]) => (
                      <p key={key} className="text-gray-700"><strong>{key}:</strong> {formatAnswerValue(value)}</p>
                    ))
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Status da inscrição</Label>
                  <Select value={selectedRegistration.registration_status} onValueChange={(value) => {
                    updateRegistration(selectedRegistration, 'registration_status', value);
                    setSelectedRegistration({ ...selectedRegistration, registration_status: value });
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{registrationStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status do pagamento</Label>
                  <Select value={selectedRegistration.payment_status} onValueChange={(value) => {
                    updateRegistration(selectedRegistration, 'payment_status', value);
                    setSelectedRegistration({ ...selectedRegistration, payment_status: value });
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{paymentStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status da autorização</Label>
                  <Select value={selectedRegistration.authorization_status} onValueChange={(value) => {
                    updateRegistration(selectedRegistration, 'authorization_status', value);
                    setSelectedRegistration({ ...selectedRegistration, authorization_status: value });
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{authorizationStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Observações administrativas</Label>
                <Textarea
                  value={selectedRegistration.admin_notes || ''}
                  onChange={(event) => setSelectedRegistration({ ...selectedRegistration, admin_notes: event.target.value })}
                  onBlur={(event) => updateRegistration(selectedRegistration, 'admin_notes', event.target.value)}
                  rows={3}
                />
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-2">Histórico</p>
                <div className="rounded-lg border border-gray-200 p-3 text-sm space-y-2 max-h-48 overflow-auto">
                  {(Array.isArray(selectedRegistration.history) ? selectedRegistration.history : []).length === 0 ? (
                    <p className="text-gray-500">Sem histórico.</p>
                  ) : (
                    selectedRegistration.history.map((item, index) => (
                      <div key={`${item.at}-${index}`}>
                        <p className="font-medium text-gray-900">{item.action}</p>
                        <p className="text-gray-500">{item.by || 'Sistema'} · {item.at ? new Date(item.at).toLocaleString('pt-BR') : ''}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
